import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "@common/store/hooks";
import MaterialIcon from "@common/components/MaterialIcon";
import Cover from "./Cover";
import { ago, lastActive, playerName, type UserSummary } from "./dashboard";
import { formatPlayed } from "./playtime";
import { useDashboard } from "./queries";

/** The game whose cover is a player's picture: their choice, else their most played. */
export function pictureGame(player: UserSummary): string | null {
  const chosen = player.picture && player.played.find((g) => g.game === player.picture);
  const most = [...player.played].sort((a, b) => b.seconds - a.seconds)[0];
  return (chosen || most)?.name ?? null;
}

/**
 * A small card about a player: picture, name, and their figures. Fixed to
 * the viewport under the name, so a list's clipping can't cut it off.
 */
function PlayerCard({ player, mine, at }: { player: UserSummary; mine: boolean; at: { left: number; top: number } }) {
  const picture = pictureGame(player);
  const active = lastActive(player);
  return (
    <div className="player-card" role="tooltip" style={{ left: Math.min(at.left, window.innerWidth - 336), top: at.top }}>
      <span className="player-card-picture">
        {picture ? <Cover name={picture} className="player-card-cover" /> : <MaterialIcon name="account_circle" />}
      </span>
      <span className="player-card-text">
        <strong>{mine ? "You" : playerName(player)}</strong>
        {player.email && <span className="muted small">{player.email}</span>}
        <span className="muted small">
          {player.secondsPlayed ? `${formatPlayed(player.secondsPlayed)} · ` : ""}
          {player.played.length} {player.played.length === 1 ? "game" : "games"}
          {active && ` · active ${ago(active)}`}
        </span>
      </span>
    </div>
  );
}

/**
 * A player's name, linking to their profile; "You" for the signed-in
 * account. Hovering it shows a small card about them, from the dashboard
 * feed, once it's loaded.
 */
export default function PlayerLink({
  sub,
  email,
  name = null,
  me,
  className = "",
}: {
  sub: string;
  email: string | null;
  name?: string | null;
  me: string | null;
  className?: string;
}) {
  const mine = sub === me;
  const { isAuthenticated } = useAppSelector((s) => s.auth);
  const feed = useDashboard(isAuthenticated);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const link = useRef<HTMLAnchorElement>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  // The card goes once the page scrolls: its place was measured.
  useEffect(() => {
    if (!at) return;
    const hide = () => setAt(null);
    document.addEventListener("scroll", hide, true);
    return () => document.removeEventListener("scroll", hide, true);
  }, [at]);
  const player = at ? (feed.data?.players.find((p) => p.sub === sub) ?? null) : null;
  // A short pause before the card, so passing over a list doesn't flicker.
  const enter = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const rect = link.current?.getBoundingClientRect();
      if (rect) setAt({ left: rect.left, top: rect.bottom + 6 });
    }, 250);
  };
  const leave = () => {
    window.clearTimeout(timer.current);
    setAt(null);
  };
  return (
    <span className="player-hover" onPointerEnter={enter} onPointerLeave={leave} onFocus={enter} onBlur={leave}>
      <Link
        ref={link}
        to={mine ? "/profile" : `/profile?${new URLSearchParams({ user: sub })}`}
        className={`player-link${className ? ` ${className}` : ""}`}
      >
        {mine ? "You" : playerName({ sub, email, name })}
      </Link>
      {player && at && <PlayerCard player={player} mine={mine} at={at} />}
    </span>
  );
}
